import { Link, useNavigate } from 'react-router-dom'
import { Lock } from 'lucide-react'
import { getItemLink, getItemId, getItemName, getItemIcon } from './utils'
import HighlightText from './HighlightText'
import noFavorite from '../../assets/images/noFavorite.png'
import favorite from '../../assets/images/yesFavorite.png'
import { useContentAccess } from '../../hooks/useApi'
import CountdownTimer from '../CountdownTimer/CountdownTimer'

interface InteractiveCategoryCardProps {
  item: any
  showingType: 'subcategories' | 'cards' | 'courses'
  hasSubscription?: boolean
  className?: string
  onCategoryClick?: (categoryId: number) => void
  onToggleFavorite?: (itemId: string) => void
  isFavorite?: boolean
  searchQuery?: string
  showLockBadge?: boolean
}

const InteractiveCategoryCard = ({
  item,
  showingType,
  hasSubscription,
  className = 'category-card',
  onCategoryClick,
  onToggleFavorite,
  isFavorite,
  searchQuery = '',
  showLockBadge,
}: InteractiveCategoryCardProps) => {
  const navigate = useNavigate()
  const { hasSubscription: userHasSubscription } = useContentAccess()

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.target as HTMLImageElement
    // Скрываем сломанное изображение
    target.style.display = 'none'
    // Показываем плейсхолдер если он есть
    const placeholder = target.parentElement?.querySelector(
      '.category-placeholder'
    )
    if (placeholder) {
      placeholder.classList.remove('hidden')
    }
  }

  const handleClick = (e: React.MouseEvent) => {
    if (showingType === 'subcategories' && onCategoryClick) {
      e.preventDefault()
      onCategoryClick(item.category_id)
      return
    }

    if (showingType === 'courses') {
      // Без подписки на подписочный курс — редирект на /subscribe
      if (isLockedBySubscription) {
        e.preventDefault()
        navigate('/subscribe')
        return
      }

      // С подпиской, но курс ещё не доступен по таймеру — блокируем
      if (isCourseNotYetAvailable) {
        e.preventDefault()
        return
      }
    }
  }

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (onToggleFavorite) {
      onToggleFavorite(getItemId(item, showingType).toString())
    }
  }

  const showFavoriteButton =
    hasSubscription && showingType === 'cards' && onToggleFavorite

  // Логика доступности курса
  const isCourse = showingType === 'courses'
  const isSubscriptionCourse = isCourse && item.base?.grant_type === 'subscription'
  const isLockedBySubscription = isSubscriptionCourse && !userHasSubscription
  const availableAfter = isCourse ? item.base?.available_after : null
  const isCourseNotYetAvailable = availableAfter
    ? new Date(availableAfter).getTime() > Date.now()
    : false

  // Данные для прогресса курса
  const courseProgress = isCourse ? item.progress : null
  const courseTotalLessons = isCourse ? (item.base?.lesson_count || item.base?.total_lessons || 0) : 0
  const courseViewedLessons = isCourse && item.access ? (courseProgress?.views || 0) : 0
  const showProgress = isCourse && item.access && courseTotalLessons > 0

  const content = (
    <>
      <div className={`category-image ${isLockedBySubscription ? 'category-image-blur' : ''}`}>
        <img
          src={getItemIcon(item, showingType)}
          alt={getItemName(item, showingType)}
          onError={handleImageError}
          loading="lazy"
          decoding="async"
        />
        <div className="category-placeholder hidden">
          {getItemName(item, showingType).charAt(0).toUpperCase()}
        </div>
      </div>
      {isLockedBySubscription ? (
        <div className="category-lock-badge category-lock-badge-left">
          <Lock size={24} color="#FFFFFF" />
        </div>
      ) : isCourseNotYetAvailable && availableAfter ? (
        <CountdownTimer targetDate={availableAfter} />
      ) : showLockBadge ? (
        <div className="category-lock-badge category-lock-badge-left">
          <Lock size={24} color="#FFFFFF" />
        </div>
      ) : null}
      <div className={`category-info ${showProgress ? 'category-info-with-progress' : ''}`}>
        <h3 className="category-name">
          <HighlightText
            text={getItemName(item, showingType)}
            searchQuery={searchQuery}
          />
        </h3>
        {showProgress && (
          <div className="course-progress-wrapper">
            <div className="course-progress-bar">
              <div
                className="course-progress-fill"
                style={{ width: `${courseTotalLessons > 0 ? (courseViewedLessons / courseTotalLessons) * 100 : 0}%` }}
              />
            </div>
            <div className="course-progress-info">
              <span className="course-progress-label">Пройдено</span>
              <span className="course-progress-count">{courseViewedLessons}/{courseTotalLessons}</span>
            </div>
          </div>
        )}
      </div>
      {showFavoriteButton && (
        <button
          className={`favorite-button ${isFavorite ? 'favorite-active' : ''}`}
          onClick={handleFavoriteClick}
          title={isFavorite ? 'Удалить из избранного' : 'Добавить в избранное'}
        >
          {isFavorite ? (
            <img src={favorite} alt="favorite" className="favorite-icon" />
          ) : (
            <img src={noFavorite} alt="noFavorite" className="favorite-icon" />
          )}
        </button>
      )}
    </>
  )

  return (
    <Link
      key={getItemId(item, showingType)}
      to={getItemLink(item, showingType)}
      className={className}
      onClick={handleClick}
    >
      {content}
    </Link>
  )
}

export default InteractiveCategoryCard
